import { defaultLocale, supportedLocales } from '@/models/Language';

import { type App, nextTick } from 'vue';
import { Composer, createI18n, I18n } from 'vue-i18n';

import { downloadTranslations, getStartingLocale } from '@/utils/i18n';

import { allSources } from '@/i18n/sources';
import merge from 'lodash.mergewith';

interface TranslationCache {
  locale: string;
  messages: Record<string, string>;
}

const locale = getStartingLocale();
const targetLocale: string = Object.keys(supportedLocales).includes(locale)
  ? locale
  : defaultLocale;

let i18n: I18n;
let cache: TranslationCache;

const initializeI18n = (
  app: App,
  options = {
    legacy: false,
    locale: targetLocale,
    fallbackLocale: defaultLocale,
    availableLocales: Object.keys(supportedLocales),
  },
) => {
  const newI18n = createI18n(options);
  setI18nLanguage(newI18n);

  i18n = newI18n;

  app.use(newI18n);
};

const setI18nLanguage = (i18n: I18n) => {
  (i18n.global as Composer).locale.value = targetLocale;
  document.querySelector('html')?.setAttribute('lang', targetLocale);
};

const isTranslationsDisabled = () =>
  new URL(window.location.href).searchParams.get('disableTranslations') === 'true';

const isCached = () => cache?.locale === targetLocale;

const setCache = (newCache: TranslationCache) => {
  cache = newCache;
};

const getSources = () => {
  return allSources;
};

const getMessages = async () => {
  const sources = getSources();
  const translations = await downloadTranslations(targetLocale).catch(() => []);

  return merge(sources, translations);
};

/**
 * Use source files on local development, otherwise download the files via CrowdIn CDN.
 * @param i18n
 * @param locale
 */
const loadMessages = async (i18n: I18n) => {
  if (isTranslationsDisabled() || isCached()) {
    return nextTick();
  }

  const messages = await getMessages();
  if (messages) {
    setCache({ locale: targetLocale, messages });
    i18n.global.setLocaleMessage(targetLocale, messages);

    setI18nLanguage(i18n);
  }

  return nextTick();
};

export { i18n, targetLocale, initializeI18n, setI18nLanguage, loadMessages };
